import { type RestModalCard, type RestMultiRowSurveyCard } from '../contracts';
import { BrowserFunnelStorage } from './browser-funnel-storage';
import { type FunnelStorage } from './storage-port';

export type FunnelLocalStateProps = {
  name: string;
  id: string;
  session: string;
};
type StoredState = {
  survey: RestMultiRowSurveyCard['provides'];
  modal: RestModalCard | undefined;
  showModal: boolean;
};
export class FunnelLocalState {
  readonly LS: FunnelStorage;

  noReloadModalTemplates = [
    'modal_confirm_cancel',
    'modal_email_send',
    'modal_email_success',
    'final_offer',
  ];

  noReloadModalNames = [
    'post_cancel',
    'confirm_cancel_custom_data',
    'confirm_cancel',
  ];

  disabled: boolean;

  name: string;

  id: string;

  session: string;

  key: string;

  state: Partial<StoredState>;

  constructor(
    { name, id, session }: FunnelLocalStateProps,
    disabled?: boolean,
    storage: FunnelStorage = new BrowserFunnelStorage()
  ) {
    this.LS = storage;
    this.disabled = disabled || session === `\${session_id}`;
    this.name = name;
    this.id = id;
    this.session = session;
    this.key = `BBLS-Funnel-${this.name}-${this.id}-${this.session}`;
    this.state = {};
  }

  didStateChange({ survey, modal, showModal }: Partial<StoredState>): boolean {
    return (
      this.state.survey !== survey ||
      this.state.modal !== modal ||
      this.state.showModal !== showModal
    );
  }

  saveState({ survey, modal, showModal }: StoredState): void {
    if (this.disabled) {
      return;
    }
    if (this.didStateChange({ survey, modal, showModal })) {
      // Save survey, modal, and modal state in LS for reload
      this.LS.put(this.key, {
        survey,
        modal,
        showModal,
      });
    }
  }

  subKey(namespace: string): string {
    return `${this.key}_${namespace}`;
  }

  has(namespace: string): boolean | undefined {
    if (this.disabled) {
      return false;
    }
    return this.LS.has(this.subKey(namespace))[1];
  }

  get<T = unknown>(namespace: string): T | undefined {
    if (this.disabled) {
      return undefined;
    }
    return this.LS.get<T>(this.subKey(namespace))[1];
  }

  /**
   * Takes an already-composed sub-key, not a namespace -- unlike `get`, `put`
   * and `has`, which apply `subKey` for you. Both callers pass a key straight
   * from `search`, so the asymmetry is load-bearing: applying `subKey` here
   * would prefix an already-prefixed key and delete nothing.
   *
   * The parameter is named accordingly rather than corrected, because
   * correcting it means changing those two call sites, and this package is
   * still being extracted. Resolve it with the answers-only store split, where
   * the whole key scheme is on the table.
   */
  del(key: string): undefined | [Error] | [null] {
    if (this.disabled) {
      return undefined;
    }
    return this.LS.del(key);
  }

  put(namespace: string, val: unknown): undefined | [Error] | [null, string] {
    if (this.disabled) {
      return undefined;
    }
    return this.LS.put(this.subKey(namespace), val);
  }

  search(namespace: string): { key?: string; value?: string }[] | null {
    if (this.disabled) {
      return null;
    }
    const reg = new RegExp(namespace);
    return this.LS.search(reg)[1];
  }

  hasSavedState(): boolean | undefined {
    if (this.disabled) {
      return false;
    }
    return this.LS.has(this.key)[1];
  }

  loadSavedState(): StoredState | null | undefined {
    if (this.hasSavedState()) {
      const [errG, savedState] = this.LS.get<StoredState>(this.key);
      if (!errG) {
        return this.cleanState(savedState);
      }
    }
    return null;
  }

  cleanState(state?: StoredState): StoredState | undefined {
    if (state) {
      const cleanState = { ...state };

      if (cleanState.survey) {
        cleanState.survey.confirmation = false;
        // zero these each time the page is loaded / refreshed
        cleanState.survey.responses = {};
      }

      if (cleanState.modal) {
        const { template, name } = cleanState.modal;
        if (
          (template && this.noReloadModalTemplates.includes(template)) ||
          (name && this.noReloadModalNames.includes(name))
        ) {
          cleanState.modal = {} as RestModalCard;
          cleanState.showModal = false;
        }
      }
      return cleanState;
    }
    return state;
  }
}
